import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  IoVideocamOutline, 
  IoCloseOutline, 
  IoResizeOutline,
  IoExpandOutline,
  IoContractOutline
} from 'react-icons/io5';
import './PersistentVideoPlayer.css';

function PersistentVideoPlayer() {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState('bottom-right'); // 'bottom-right', 'bottom-left', 'top-right', 'top-left'
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Get initial size based on screen width
  const getInitialSize = () => {
    if (typeof window !== 'undefined') {
      const isMobile = window.innerWidth <= 640;
      if (isMobile) {
        return { width: 160, height: 90 }; // Small default for mobile
      }
    }
    return { width: 400, height: 225 }; // Default size (16:9 ratio)
  };
  
  const [size, setSize] = useState(getInitialSize);
  const [isResizing, setIsResizing] = useState(false);
  const playerRef = useRef(null);
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });
  
  const videoId = '-fePqqGqemE';
  // Add autoplay parameter (mute=0 means sound on, but browsers may still require user interaction)
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=0`;

  // Load state from localStorage on mount
  useEffect(() => {
    try {
      const savedState = localStorage.getItem('videoPlayerState');
      if (savedState) {
        const state = JSON.parse(savedState);
        setIsMinimized(state.isMinimized || false);
        setIsVisible(state.isVisible || false);
        setPosition(state.position || 'bottom-right');
        if (state.size) {
          setSize(state.size);
        }
      }
      setIsInitialized(true);
    } catch (error) {
      console.error('Error loading video player state:', error);
      // Reset to defaults if there's an error
      setIsMinimized(false);
      setIsVisible(false);
      setPosition('bottom-right');
      setIsInitialized(true);
    }
  }, []);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('videoPlayerState', JSON.stringify({
        isMinimized,
        isVisible,
        position,
        size
      }));
    } catch (error) {
      console.error('Error saving video player state:', error);
    }
  }, [isMinimized, isVisible, position, size]);

  // Handle resize start
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    
    const startX = e.touches ? e.touches[0].clientX : e.clientX;
    const startY = e.touches ? e.touches[0].clientY : e.clientY;
    
    resizeStartRef.current = {
      x: startX,
      y: startY,
      width: size.width,
      height: size.height
    };
  }, [size]);

  // Handle resize move
  const handleResizeMove = useCallback((e) => {
    if (!isResizing) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const currentX = e.touches ? e.touches[0].clientX : e.clientX;
    const currentY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const deltaX = currentX - resizeStartRef.current.x;
    const deltaY = currentY - resizeStartRef.current.y;
    
    // Calculate new size (resize from bottom-right corner)
    const newWidth = Math.max(200, Math.min(800, resizeStartRef.current.width + deltaX));
    const newHeight = Math.max(112, Math.min(450, resizeStartRef.current.height + deltaY));
    
    // Maintain 16:9 aspect ratio
    const aspectRatio = 16 / 9;
    const heightFromWidth = newWidth / aspectRatio;
    const widthFromHeight = newHeight * aspectRatio;
    
    // Use the dimension that results in the smaller change
    if (Math.abs(newWidth - resizeStartRef.current.width) > Math.abs(newHeight - resizeStartRef.current.height)) {
      setSize({ width: newWidth, height: heightFromWidth });
    } else {
      setSize({ width: widthFromHeight, height: newHeight });
    }
  }, [isResizing]);

  // Handle resize end
  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Add event listeners for resize
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.addEventListener('touchmove', handleResizeMove, { passive: false });
      document.addEventListener('touchend', handleResizeEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
        document.removeEventListener('touchmove', handleResizeMove);
        document.removeEventListener('touchend', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  const handlePositionChange = (newPosition) => {
    setPosition(newPosition);
  };

  // Don't render until initialized to avoid flash
  if (!isInitialized) {
    return null;
  }

  // Always show button when video is not visible
  if (!isVisible) {
    // Show floating button to start the stream
    return (
      <div className={`persistent-video-trigger persistent-video-trigger-${position}`}>
        <button 
          className="persistent-video-trigger-btn"
          onClick={toggleVisibility}
          aria-label="Start Live Stream"
          title="Start Live Stream"
          type="button"
        >
          <IoVideocamOutline />
          <span>Watch Stream</span>
        </button>
      </div>
    );
  }

  return (
    <div 
      ref={playerRef}
      className={`persistent-video-player persistent-video-player-${position} ${isMinimized ? 'minimized' : ''} ${isResizing ? 'resizing' : ''}`}
      style={{
        width: isMinimized ? 'auto' : `${size.width}px`,
        height: isMinimized ? 'auto' : 'auto'
      }}
    >
      <div className="persistent-video-header">
        <div className="persistent-video-title">
          <IoVideocamOutline />
          <span>Live Stream</span>
        </div>
        <div className="persistent-video-controls">
          {/* Position controls */}
          <div className="persistent-video-position-controls">
            <button
              className={`position-btn ${position === 'top-left' ? 'active' : ''}`}
              onClick={() => handlePositionChange('top-left')}
              title="Top Left"
              aria-label="Move to top left"
            >
              ↖
            </button>
            <button
              className={`position-btn ${position === 'top-right' ? 'active' : ''}`}
              onClick={() => handlePositionChange('top-right')}
              title="Top Right"
              aria-label="Move to top right"
            >
              ↗
            </button>
            <button
              className={`position-btn ${position === 'bottom-left' ? 'active' : ''}`}
              onClick={() => handlePositionChange('bottom-left')}
              title="Bottom Left"
              aria-label="Move to bottom left"
            >
              ↙
            </button>
            <button
              className={`position-btn ${position === 'bottom-right' ? 'active' : ''}`}
              onClick={() => handlePositionChange('bottom-right')}
              title="Bottom Right"
              aria-label="Move to bottom right"
            >
              ↘
            </button>
          </div>
          {/* Minimize/Maximize button */}
          <button
            className="persistent-video-minimize-btn"
            onClick={toggleMinimize}
            aria-label={isMinimized ? 'Maximize' : 'Minimize'}
            title={isMinimized ? 'Maximize' : 'Minimize'}
          >
            {isMinimized ? <IoExpandOutline /> : <IoContractOutline />}
          </button>
          {/* Close button */}
          <button
            className="persistent-video-close-btn"
            onClick={toggleVisibility}
            aria-label="Close"
            title="Close"
          >
            <IoCloseOutline />
          </button>
        </div>
      </div>
      {!isMinimized && (
        <div 
          className="persistent-video-content"
          style={{ height: `${size.height}px` }}
        >
          <div className="persistent-video-wrapper">
            <iframe
              className="persistent-video-iframe"
              src={embedUrl}
              title="Live Game Show Stream"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          </div>
          {/* Resize handle */}
          <div 
            className="persistent-video-resize-handle"
            onMouseDown={handleResizeStart}
            onTouchStart={handleResizeStart}
            aria-label="Resize video player"
            title="Drag to resize"
          >
            <IoResizeOutline />
          </div>
        </div>
      )}
    </div>
  );
}

export default PersistentVideoPlayer;

